import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  useTheme,
  alpha,
  Container,
  Fade,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  ShowChart as ShowChartIcon,
  Assessment as AssessmentIcon,
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  Factory as ProductionIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as ShoppingCartIcon,
  MonetizationOn as SalesIcon,
  LocalMall as ProductsIcon
} from '@mui/icons-material';

const DashboardShowCase = () => {
  const theme = useTheme();
  const [activeSection, setActiveSection] = useState('sales');

  // Dummy data that matches the real dashboard structure
  const dummyData = {
    sales: {
      salesMetrics: {
        totalRevenue: 2847650,
        averageOrderValue: 1247.80,
        totalCustomers: 342,
        totalOrders: 2284,
        monthlyGrowth: 12.4
      },
      monthlySales: [
        { month: 'Jul 2024', revenue: 425000, orders: 340 },
        { month: 'Aug 2024', revenue: 389000, orders: 312 },
        { month: 'Sep 2024', revenue: 467000, orders: 374 },
        { month: 'Oct 2024', revenue: 523000, orders: 419 },
        { month: 'Nov 2024', revenue: 498000, orders: 399 },
        { month: 'Dec 2024', revenue: 545000, orders: 436 }
      ],
      topOrders: [
        { id: 1, invoice_number: 'INV-2024-0892', total_amount: 15420, invoice_date: '2024-12-15', customers: { name: 'Global Beauty Corp', company: null } },
        { id: 2, invoice_number: 'INV-2024-0891', total_amount: 12850, invoice_date: '2024-12-14', customers: { name: 'Premium Wellness Ltd', company: null } },
        { id: 3, invoice_number: 'INV-2024-0890', total_amount: 11200, invoice_date: '2024-12-13', customers: { name: 'Luxury Brands Inc', company: null } },
        { id: 4, invoice_number: 'INV-2024-0889', total_amount: 9875, invoice_date: '2024-12-12', customers: { name: 'Retail Solutions Co', company: null } },
        { id: 5, invoice_number: 'INV-2024-0888', total_amount: 8950, invoice_date: '2024-12-11', customers: { name: 'Beauty Distributors LLC', company: null } }
      ],
      topCountries: [
        { country: 'United States', orders: 1247, revenue: 1547200 },
        { country: 'Canada', orders: 342, revenue: 425800 },
        { country: 'United Kingdom', orders: 298, revenue: 387500 },
        { country: 'Australia', orders: 187, revenue: 245900 },
        { country: 'Germany', orders: 156, revenue: 198400 }
      ],
      topProducts: [
        { product_name: 'Premium Face Serum', total_quantity: 1247, total_revenue: 498800 },
        { product_name: 'Luxury Body Lotion', total_quantity: 892, total_revenue: 356800 },
        { product_name: 'Anti-Aging Cream', total_quantity: 756, total_revenue: 302400 },
        { product_name: 'Hydrating Moisturizer', total_quantity: 634, total_revenue: 253600 },
        { product_name: 'Vitamin C Complex', total_quantity: 523, total_revenue: 209200 }
      ]
    },
    products: {
      productMetrics: {
        totalRevenue: 1623400,
        totalUnitsSold: 4852,
        totalProducts: 147,
        activeProducts: 134,
        totalBrands: 12,
        totalCategories: 8,
        averageProductPrice: 67.50,
        inHouseProducts: 89,
        privateLabelProducts: 58
      },
      bestSellersByRevenue: [
        { product_name: 'Premium Face Serum', total_revenue: 498800, total_units: 1247, sku: 'PFS-001' },
        { product_name: 'Luxury Body Lotion', total_revenue: 356800, total_units: 892, sku: 'LBL-002' },
        { product_name: 'Anti-Aging Cream', total_revenue: 302400, total_units: 756, sku: 'AAC-003' },
        { product_name: 'Hydrating Moisturizer', total_revenue: 253600, total_units: 634, sku: 'HM-004' },
        { product_name: 'Vitamin C Complex', total_revenue: 209200, total_units: 523, sku: 'VCC-005' }
      ],
      brandAnalytics: [
        { brand_name: 'Luxe Skincare', total_revenue: 847200, product_count: 23 },
        { brand_name: 'Natural Beauty Co', total_revenue: 456300, product_count: 18 },
        { brand_name: 'Premium Care', total_revenue: 234500, product_count: 15 },
        { brand_name: 'Organic Essentials', total_revenue: 167800, product_count: 12 },
        { brand_name: 'Youth Formula', total_revenue: 123400, product_count: 9 }
      ],
      categoryAnalytics: [
        { category_name: 'Face Care', total_revenue: 1245600, product_count: 45 },
        { category_name: 'Body Care', total_revenue: 678900, product_count: 32 },
        { category_name: 'Hair Care', total_revenue: 456700, product_count: 28 },
        { category_name: 'Sun Care', total_revenue: 234500, product_count: 18 },
        { category_name: 'Anti-Aging', total_revenue: 167800, product_count: 24 }
      ]
    },
    inventory: {
      summary: {
        totalValue: 847650,
        totalItems: 15847,
        lowStockItems: 23,
        categories: {
          bottles: { count: 5847, value: 234500 },
          caps: { count: 3245, value: 45600 },
          labels: { count: 2156, value: 67800 },
          products: { count: 1247, value: 345700 },
          rawMaterials: { count: 2847, value: 123400 },
          supplies: { count: 505, value: 30650 }
        }
      },
      mostExpensive: [
        { name: 'Premium Glass Bottles 50ml', type: 'Bottles', unitCost: 12.50, supplier: 'Glass Solutions Inc' },
        { name: 'Luxury Pump Caps', type: 'Caps', unitCost: 8.75, supplier: 'Cap Innovations' },
        { name: 'Holographic Labels', type: 'Labels', unitCost: 3.25, supplier: 'Label Masters' },
        { name: 'Organic Argan Oil', type: 'Raw Materials', unitCost: 45.00, supplier: 'Natural Extracts Co' },
        { name: 'Anti-Aging Serum', type: 'Products', unitCost: 67.50, supplier: 'Luxe Skincare' }
      ],
      recentTransactions: [
        { id: 1, type: 'received', item: 'Premium Glass Bottles 50ml', category: 'Bottles', quantity: 500, cost: 12.50, date: '2024-12-15' },
        { id: 2, type: 'used', item: 'Luxury Pump Caps', category: 'Caps', quantity: 250, cost: 8.75, date: '2024-12-14' },
        { id: 3, type: 'received', item: 'Organic Argan Oil', category: 'Raw Materials', quantity: 100, cost: 45.00, date: '2024-12-13' }
      ]
    },
    formulations: {
      totalFormulations: 67,
      formulationsByStatus: [
        { status: 'approved', count: 23 },
        { status: 'in_review', count: 18 },
        { status: 'draft', count: 21 },
        { status: 'rejected', count: 5 }
      ],
      progressMetrics: {
        averageProgress: 67.8,
        completedFormulations: 23,
        inProgressFormulations: 18
      },
      testingMetrics: {
        totalTests: 156,
        passedTests: 134,
        failedTests: 15,
        passRate: 85.9
      },
      topIngredients: [
        { name: 'Hyaluronic Acid', formulationCount: 34, averagePercentage: 2.5 },
        { name: 'Vitamin E', formulationCount: 28, averagePercentage: 1.8 },
        { name: 'Glycerin', formulationCount: 25, averagePercentage: 5.2 },
        { name: 'Retinol', formulationCount: 19, averagePercentage: 0.8 },
        { name: 'Niacinamide', formulationCount: 16, averagePercentage: 3.1 }
      ],
      recentActivity: [
        { id: 1, name: 'Advanced Anti-Aging Serum', status: 'approved', progress: 100, updated_at: '2024-12-15' },
        { id: 2, name: 'Hydrating Face Cream', status: 'in_review', progress: 85, updated_at: '2024-12-14' },
        { id: 3, name: 'Vitamin C Brightening Serum', status: 'in_review', progress: 78, updated_at: '2024-12-13' },
        { id: 4, name: 'Gentle Cleansing Foam', status: 'draft', progress: 45, updated_at: '2024-12-12' },
        { id: 5, name: 'Peptide Eye Cream', status: 'draft', progress: 32, updated_at: '2024-12-11' }
      ]
    },
    production: {
      summary: {
        totalProductionValue: 1456700,
        totalBatches: 89,
        totalLineRuns: 156,
        averageYield: 94.2,
        activeDeviations: 7,
        batchMetrics: {
          completed: 67,
          inProgress: 15,
          failed: 7,
          totalCost: 856700
        },
        lineMetrics: {
          completed: 134,
          inProgress: 18,
          failed: 4,
          totalCost: 600000
        }
      },
      recentBatches: [
        { batch_number: 'B-2024-0234', status: 'completed', yield_percentage: 96.5, totalCost: 12400, formulations: { products: { name: 'Premium Face Serum' } }, manufacture_date: '2024-12-15' },
        { batch_number: 'B-2024-0233', status: 'in_production', yield_percentage: null, totalCost: 8900, formulations: { products: { name: 'Luxury Body Lotion' } }, manufacture_date: '2024-12-14' },
        { batch_number: 'B-2024-0232', status: 'completed', yield_percentage: 92.1, totalCost: 15600, formulations: { products: { name: 'Anti-Aging Cream' } }, manufacture_date: '2024-12-13' },
        { batch_number: 'B-2024-0231', status: 'quality_testing', yield_percentage: 94.8, totalCost: 11200, formulations: { products: { name: 'Hydrating Moisturizer' } }, manufacture_date: '2024-12-12' }
      ],
      recentLineRuns: [
        { batch_number: 'L-2024-0456', status: 'completed', efficiency: 97.2, unitsProduced: 2400, totalCost: 5600, products: { name: 'Premium Face Serum' } },
        { batch_number: 'L-2024-0455', status: 'in_production', efficiency: null, unitsProduced: 0, totalCost: 4200, products: { name: 'Luxury Body Lotion' } },
        { batch_number: 'L-2024-0454', status: 'completed', efficiency: 93.8, unitsProduced: 1800, totalCost: 3900, products: { name: 'Vitamin C Complex' } }
      ],
      qualityMetrics: [
        { test_type: 'Viscosity Test', final_status: 'PASS', product_name: 'Premium Face Serum', test_datetime: '2024-12-15', tested_by_name: 'Sarah Johnson' },
        { test_type: 'pH Balance', final_status: 'PASS', product_name: 'Luxury Body Lotion', test_datetime: '2024-12-14', tested_by_name: 'Mike Chen' },
        { test_type: 'Stability Test', final_status: 'REVIEW', product_name: 'Anti-Aging Cream', test_datetime: '2024-12-13', tested_by_name: 'Emma Wilson' }
      ],
      deviations: [
        { issue_type: 'temperature_deviation', batchNumber: 'B-2024-0230', status: 'investigating', issue_severity: 'medium', flagged_at: '2024-12-12' },
        { issue_type: 'mixing_time_variance', batchNumber: 'B-2024-0229', status: 'resolved', issue_severity: 'low', flagged_at: '2024-12-11' }
      ]
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const navigationItems = [
    { id: 'sales', label: 'Sales Analytics', icon: SalesIcon, color: '#2196F3' },
    { id: 'products', label: 'Product Analytics', icon: ProductsIcon, color: '#FF9800' },
    { id: 'inventory', label: 'Inventory Analytics', icon: InventoryIcon, color: '#607D8B' },
    { id: 'formulations', label: 'Formulation Analytics', icon: ScienceIcon, color: '#9C27B0' },
    { id: 'production', label: 'Production Analytics', icon: ProductionIcon, color: '#E91E63' }
  ];

  return (
    <Box sx={{ width: '100%', py: { xs: 4, md: 8 }, display: 'flex', justifyContent: 'center' }}>
      <Fade in timeout={800}>
        <Box sx={{ maxWidth: '1400px', width: '100%', mx: 'auto', px: { xs: 2, sm: 3 } }}>
          {/* Header Section */}
          <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ 
              fontWeight: 500, 
              color: 'text.primary', 
              mb: { xs: 1.5, md: 2 },
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' },
              letterSpacing: '-0.02em',
              px: { xs: 1, md: 0 }
            }}>
              Powerful Analytics Dashboard
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              px: { xs: 2, md: 0 }
            }}>
              Get comprehensive insights into your business performance with real-time analytics and beautiful visualizations
            </Typography>
          </Box>

          {/* Dashboard Preview */}
          <Card sx={{ 
            borderRadius: { xs: 2, md: 4 },
            border: `1px solid ${theme.palette.grey[300]}`,
            background: 'white',
            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.1)}`,
            overflow: 'hidden'
          }}>
            {/* Dashboard Header */}
            <Box sx={{ 
              p: { xs: 2, sm: 3, md: 4 }, 
              bgcolor: alpha(theme.palette.grey[100], 0.8),
              borderBottom: `1px solid ${theme.palette.grey[300]}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                <Box sx={{ 
                  p: { xs: 1, md: 1.5 }, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <DashboardIcon sx={{ fontSize: { xs: 20, md: 24 }, color: 'text.secondary' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 500, 
                    color: 'primary.main', 
                    mb: 0.5,
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.8rem' }
                  }}>
                    Analytics Dashboard
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: 'text.secondary', 
                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                    fontWeight: 500,
                    display: { xs: 'none', sm: 'block' }
                  }}>
                    Your Business Intelligence Platform
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Navigation and Content */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { xs: 'auto', md: 600 } }}>
              {/* Navigation Sidebar - Horizontal on mobile, vertical on desktop */}
              <Box sx={{ 
                width: { xs: '100%', md: '180px' },
                minWidth: { xs: 'auto', md: '180px' },
                p: { xs: 2, md: 2 },
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                gap: 1,
                borderRight: { xs: 'none', md: `1px solid ${theme.palette.grey[300]}` },
                borderBottom: { xs: `1px solid ${theme.palette.grey[300]}`, md: 'none' },
                overflowX: { xs: 'auto', md: 'visible' },
                overflowY: 'visible'
              }}>
                {navigationItems.map((section) => {
                  const IconComponent = section.icon;
                  const isActive = activeSection === section.id;
                  
                  return (
                    <Button
                      key={section.id}
                      fullWidth
                      onClick={() => setActiveSection(section.id)}
                      sx={{
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        py: { xs: 1, md: 1.5 },
                        px: { xs: 1.5, md: 2 },
                        borderRadius: 2,
                        color: isActive ? section.color : 'text.secondary',
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                        backgroundColor: isActive ? `${section.color}08` : 'transparent',
                        border: isActive ? `1px solid ${section.color}30` : '1px solid transparent',
                        transition: 'all 0.2s ease',
                        textTransform: 'none',
                        minWidth: { xs: 'auto', md: 'auto' },
                        whiteSpace: { xs: 'nowrap', md: 'normal' },
                        '&:hover': {
                          backgroundColor: `${section.color}12`,
                          color: section.color,
                          borderColor: `${section.color}40`
                        }
                      }}
                    >
                      <IconComponent sx={{ 
                        fontSize: { xs: 16, md: 18 }, 
                        mr: { xs: 0.5, sm: 1, md: 1.5 },
                        color: 'inherit'
                      }} />
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {section.label}
                      </Box>
                    </Button>
                  );
                })}
              </Box>

              {/* Dashboard Content */}
              <Box sx={{ flex: 1, p: { xs: 2, sm: 3, md: 4 } }}>
                {/* Sales Analytics Section */}
                {activeSection === 'sales' && (
                  <Box>
                    {/* Section Header */}
                    <Box sx={{ mb: { xs: 3, md: 4 }, pb: 2, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SalesIcon sx={{ fontSize: { xs: 24, md: 32 }, color: '#2196F3', mr: { xs: 1, md: 2 } }} />
                        <Typography variant="h4" sx={{ 
                          fontWeight: 500, 
                          color: '#2196F3', 
                          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                          letterSpacing: '-0.3px'
                        }}>
                          Sales Analytics
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ 
                        color: 'text.secondary', 
                        fontSize: { xs: '0.9rem', md: '1.1rem' },
                        display: { xs: 'none', sm: 'block' }
                      }}>
                        Revenue trends, customer insights, and sales performance metrics
                      </Typography>
                    </Box>

                    {/* Sales Metrics Cards */}
                    <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: { xs: 2, md: 3 },
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: { xs: 'none', md: 'translateY(-4px)' },
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                              <Box sx={{ 
                                p: { xs: 1, md: 1.5 }, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                mr: { xs: 1.5, md: 2 }
                              }}>
                                <AttachMoneyIcon sx={{ fontSize: { xs: 20, md: 24 }, color: 'success.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
                                Total Revenue
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'success.main',
                              mb: 1,
                              fontSize: { xs: '1.5rem', md: '2.125rem' }
                            }}>
                              {formatCurrency(dummyData.sales.salesMetrics.totalRevenue)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TrendingUpIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'success.main', mr: 0.5 }} />
                              <Typography variant="body2" sx={{ 
                                color: 'success.main',
                                fontWeight: 500,
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                              }}>
                                {dummyData.sales.salesMetrics.monthlyGrowth}% vs last month
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                mr: 2
                              }}>
                                <ShowChartIcon sx={{ fontSize: 24, color: 'info.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Average Order Value
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'info.main',
                              mb: 1
                            }}>
                              {formatCurrency(dummyData.sales.salesMetrics.averageOrderValue)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Per order
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                mr: 2
                              }}>
                                <ReceiptIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Orders
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'warning.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.sales.salesMetrics.totalOrders)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Invoices processed
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                mr: 2
                              }}>
                                <AccountBalanceIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Customers
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'primary.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.sales.salesMetrics.totalCustomers)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Active customers
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Charts and Tables Grid */}
                    <Grid container spacing={{ xs: 2, md: 4 }}>
                      {/* Monthly Sales Chart */}
                      <Grid item xs={12} lg={8}>
                        <Card sx={{ 
                          borderRadius: { xs: 2, md: 3 },
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: { xs: 'none', md: 'translateY(-2px)' },
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: { xs: 2, md: 3 },
                              fontSize: { xs: '1.1rem', md: '1.5rem' }
                            }}>
                              Monthly Sales Trend
                            </Typography>
                            <Box sx={{ 
                              height: { xs: 250, md: 350 }, 
                              width: '100%',
                              bgcolor: 'white',
                              borderRadius: 2,
                              p: { xs: 1, md: 2 },
                              border: `1px solid ${theme.palette.grey[200]}`,
                              overflowX: 'auto'
                            }}>
                              {/* Chart Container */}
                              <Box sx={{ 
                                display: 'flex', 
                                height: '100%',
                                gap: 2
                              }}>
                                {/* Y-Axis Labels */}
                                <Box sx={{ 
                                  width: 60, 
                                  display: 'flex', 
                                  flexDirection: 'column-reverse',
                                  justifyContent: 'space-between',
                                  py: 2,
                                  pr: 1
                                }}>
                                  {Array.from({ length: 5 }, (_, i) => {
                                    const maxRevenue = Math.max(...dummyData.sales.monthlySales.map(m => m.revenue), 100);
                                    const value = (maxRevenue / 4) * i;
                                    return (
                                      <Typography 
                                        key={i} 
                                        variant="caption" 
                                        sx={{ 
                                          color: 'text.secondary',
                                          fontSize: '0.75rem',
                                          textAlign: 'right',
                                          lineHeight: 1
                                        }}
                                      >
                                        ${value >= 1000 ? `${(value/1000).toFixed(0)}K` : Math.round(value)}
                                      </Typography>
                                    );
                                  })}
                                </Box>
                                
                                {/* Chart Area */}
                                <Box sx={{ 
                                  flex: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  height: '100%'
                                }}>
                                  {/* Bars Container */}
                                  <Box sx={{ 
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'end',
                                    gap: 3,
                                    px: 2,
                                    pb: 1,
                                    position: 'relative',
                                    borderLeft: `2px solid ${theme.palette.grey[200]}`,
                                    borderBottom: `2px solid ${theme.palette.grey[200]}`
                                  }}>
                                    {/* Grid Lines */}
                                    {Array.from({ length: 4 }, (_, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          position: 'absolute',
                                          left: 0,
                                          right: 0,
                                          bottom: `${(i + 1) * 25}%`,
                                          height: '1px',
                                          bgcolor: alpha(theme.palette.grey[300], 0.5),
                                          zIndex: 0
                                        }}
                                      />
                                    ))}
                                    
                                    {/* Bars */}
                                    {dummyData.sales.monthlySales.map((monthData, index) => {
                                      const maxRevenue = Math.max(...dummyData.sales.monthlySales.map(m => m.revenue), 100);
                                      const barHeight = Math.max((monthData.revenue / maxRevenue) * 100, 8);
                                      
                                      return (
                                        <Box 
                                          key={index} 
                                          sx={{ 
                                            flex: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            zIndex: 1
                                          }}
                                        >
                                          {/* Bar */}
                                          <Box
                                            sx={{
                                              width: 32,
                                              height: `${barHeight}%`,
                                              minHeight: '250px',
                                              bgcolor: '#4caf50',
                                              borderRadius: '3px 3px 0 0',
                                              cursor: 'default',
                                              transition: 'all 0.2s ease',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                              '&:hover': {
                                                bgcolor: '#66bb6a',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                              }
                                            }}
                                          />
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                  
                                  {/* X-Axis Labels */}
                                  <Box sx={{ 
                                    display: 'flex',
                                    gap: 3,
                                    px: 2,
                                    pt: 1.5,
                                    ml: 2
                                  }}>
                                    {dummyData.sales.monthlySales.map((monthData, index) => (
                                      <Box key={index} sx={{ flex: 1, textAlign: 'center' }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            fontSize: '0.8rem'
                                          }}
                                        >
                                          {monthData.month}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Top Orders */}
                      <Grid item xs={12} lg={4}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Top Orders
                            </Typography>
                            <Stack spacing={2}>
                              {dummyData.sales.topOrders.slice(0, 3).map((order, index) => (
                                <Box key={order.id} sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: alpha(theme.palette.grey[100], 0.5),
                                  border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                  }
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                      {order.invoice_number}
                                    </Typography>
                                    <Chip 
                                      label={`#${index + 1}`}
                                      size="small"
                                      sx={{ 
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: 'primary.main',
                                        fontWeight: 500
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {order.customers?.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                                      {formatCurrency(order.total_amount)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      {formatDate(order.invoice_date)}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Product Analytics Section */}
                {activeSection === 'products' && (
                  <Box>
                    {/* Section Header */}
                    <Box sx={{ mb: 4, pb: 2, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ProductsIcon sx={{ fontSize: 32, color: '#FF9800', mr: 2 }} />
                        <Typography variant="h4" sx={{ 
                          fontWeight: 500, 
                          color: '#FF9800', 
                          fontSize: '2rem',
                          letterSpacing: '-0.3px'
                        }}>
                          Product Analytics
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '1.1rem'
                      }}>
                        Best-selling products, inventory insights, and performance analysis
                      </Typography>
                    </Box>

                    {/* Product Metrics Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                mr: 2
                              }}>
                                <AssessmentIcon sx={{ fontSize: 24, color: 'success.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Product Revenue
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'success.main',
                              mb: 1
                            }}>
                              {formatCurrency(dummyData.products.productMetrics.totalRevenue)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Total sales revenue
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                mr: 2
                              }}>
                                <ShoppingCartIcon sx={{ fontSize: 24, color: 'info.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Units Sold
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'info.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.products.productMetrics.totalUnitsSold)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Total units sold
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                mr: 2
                              }}>
                                <InventoryIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Products
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'warning.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.products.productMetrics.totalProducts)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              In catalog
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                mr: 2
                              }}>
                                <TrendingUpIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Active Products
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'primary.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.products.productMetrics.activeProducts)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Currently active
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Product Analytics Tables */}
                    <Grid container spacing={4}>
                      {/* Top Brands */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: { xs: 2, md: 3 },
                              fontSize: { xs: '1.1rem', md: '1.5rem' }
                            }}>
                              Top Brands by Revenue
                            </Typography>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Brand</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Products</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Revenue</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dummyData.products.brandAnalytics.map((brand, index) => (
                                    <TableRow key={brand.brand_name} sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                                      }
                                    }}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Chip 
                                            label={index + 1}
                                            size="small"
                                            sx={{ 
                                              mr: 1,
                                              bgcolor: alpha('#2196F3', 0.1),
                                              color: '#2196F3',
                                              fontWeight: 500,
                                              minWidth: 24
                                            }}
                                          />
                                          {brand.brand_name}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                                        {formatNumber(brand.product_count)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500, color: 'success.main' }}>
                                        {formatCurrency(brand.total_revenue)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Top Categories */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Top Categories by Revenue
                            </Typography>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Category</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Products</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Revenue</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dummyData.products.categoryAnalytics.map((category, index) => (
                                    <TableRow key={category.category_name} sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                                      }
                                    }}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Chip 
                                            label={index + 1}
                                            size="small"
                                            sx={{ 
                                              mr: 1,
                                              bgcolor: alpha('#9C27B0', 0.1),
                                              color: '#9C27B0',
                                              fontWeight: 500,
                                              minWidth: 24
                                            }}
                                          />
                                          {category.category_name}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                                        {formatNumber(category.product_count)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500, color: 'success.main' }}>
                                        {formatCurrency(category.total_revenue)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Production Analytics Section */}
                {activeSection === 'production' && (
                  <Box>
                    {/* Section Header */}
                    <Box sx={{ mb: 4, pb: 2, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ProductionIcon sx={{ fontSize: 32, color: '#E91E63', mr: 2 }} />
                        <Typography variant="h4" sx={{ 
                          fontWeight: 500, 
                          color: '#E91E63', 
                          fontSize: '2rem',
                          letterSpacing: '-0.3px'
                        }}>
                          Production Analytics
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '1.1rem'
                      }}>
                        Batch production, line runs, quality control, costs, and manufacturing efficiency
                      </Typography>
                    </Box>

                    {/* Production Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                mr: 2
                              }}>
                                <AttachMoneyIcon sx={{ fontSize: 24, color: 'success.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Production Value
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'success.main',
                              mb: 1
                            }}>
                              {formatCurrency(dummyData.production.summary.totalProductionValue)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Batch + Line Production
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                mr: 2
                              }}>
                                <ProductionIcon sx={{ fontSize: 24, color: 'info.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Batches
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'info.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.production.summary.totalBatches)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Production batches
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                mr: 2
                              }}>
                                <TrendingUpIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Average Yield
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'primary.main',
                              mb: 1
                            }}>
                              {dummyData.production.summary.averageYield.toFixed(1)}%
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Production efficiency
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                mr: 2
                              }}>
                                <TrendingDownIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Active Deviations
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'warning.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.production.summary.activeDeviations)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Require attention
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Production Analytics Grid */}
                    <Grid container spacing={4}>
                      {/* Batch Production Metrics */}
                      <Grid item xs={12} md={4}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Batch Production Metrics
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ 
                                    fontWeight: 500, 
                                    color: '#4CAF50',
                                    mb: 1
                                  }}>
                                    {formatNumber(dummyData.production.summary.batchMetrics.completed)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Completed
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ 
                                    fontWeight: 500, 
                                    color: '#2196F3',
                                    mb: 1
                                  }}>
                                    {formatNumber(dummyData.production.summary.batchMetrics.inProgress)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    In Progress
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Total Cost:
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 500 }}>
                                    {formatCurrency(dummyData.production.summary.batchMetrics.totalCost)}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Line Production Metrics */}
                      <Grid item xs={12} md={4}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Line Production Metrics
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ 
                                    fontWeight: 500, 
                                    color: '#4CAF50',
                                    mb: 1
                                  }}>
                                    {formatNumber(dummyData.production.summary.lineMetrics.completed)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Completed
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ 
                                    fontWeight: 500, 
                                    color: '#2196F3',
                                    mb: 1
                                  }}>
                                    {formatNumber(dummyData.production.summary.lineMetrics.inProgress)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    In Progress
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Total Cost:
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 500 }}>
                                    {formatCurrency(dummyData.production.summary.lineMetrics.totalCost)}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Yield Performance */}
                      <Grid item xs={12} md={4}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Yield Performance
                            </Typography>
                            
                            {/* Average Yield */}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  Average Yield
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 500, 
                                  color: dummyData.production.summary.averageYield >= 95 ? '#4CAF50' : dummyData.production.summary.averageYield >= 85 ? '#FF9800' : '#F44336'
                                }}>
                                  {dummyData.production.summary.averageYield.toFixed(1)}%
                                </Typography>
                              </Box>
                            </Box>

                            {/* Batch vs Line Yield Comparison */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                                Yield by Production Type
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#2196F3', 0.1) }}>
                                    <Typography variant="h5" sx={{ fontWeight: 500, color: '#2196F3' }}>
                                      93.8%
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Batch Production
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={6}>
                                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#E91E63', 0.1) }}>
                                    <Typography variant="h5" sx={{ fontWeight: 500, color: '#E91E63' }}>
                                      94.6%
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Line Production
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>

                            {/* Right First Time Rate */}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                Right First Time Rate
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h4" sx={{ 
                                  fontWeight: 500, 
                                  color: '#4CAF50'
                                }}>
                                  42
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  / 67 batches
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Recent Batches */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Recent Production Batches
                            </Typography>
                            <Stack spacing={2}>
                              {dummyData.production.recentBatches.map((batch, index) => (
                                <Box key={index} sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: alpha(theme.palette.grey[100], 0.5),
                                  border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                  }
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                      {batch.batch_number}
                                    </Typography>
                                    <Chip 
                                      label={batch.status}
                                      size="small"
                                      sx={{ 
                                        bgcolor: batch.status === 'completed' ? alpha('#4CAF50', 0.1) : batch.status === 'in_production' ? alpha('#2196F3', 0.1) : alpha('#FF9800', 0.1),
                                        color: batch.status === 'completed' ? '#4CAF50' : batch.status === 'in_production' ? '#2196F3' : '#FF9800',
                                        fontWeight: 500
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {batch.formulations?.products?.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'info.main' }}>
                                      Yield: {batch.yield_percentage ? `${batch.yield_percentage.toFixed(1)}%` : 'TBD'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                                      {formatCurrency(batch.totalCost)}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Quality Control Results */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Quality Control Results
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                              <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#4CAF50', 0.1) }}>
                                  <Typography variant="h4" sx={{ fontWeight: 500, color: '#4CAF50' }}>
                                    {dummyData.production.qualityMetrics.filter(qc => qc.final_status === 'PASS').length}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Passed
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#FF9800', 0.1) }}>
                                  <Typography variant="h4" sx={{ fontWeight: 500, color: '#FF9800' }}>
                                    {dummyData.production.qualityMetrics.filter(qc => qc.final_status === 'REVIEW').length}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Review
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: alpha('#F44336', 0.1) }}>
                                  <Typography variant="h4" sx={{ fontWeight: 500, color: '#F44336' }}>
                                    0
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Failed
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            {dummyData.production.qualityMetrics.map((qc, index) => (
                              <Box key={index} sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                p: 2,
                                mb: 1,
                                borderRadius: 2,
                                bgcolor: alpha(qc.final_status === 'PASS' ? '#4CAF50' : '#FF9800', 0.05),
                                border: `1px solid ${alpha(qc.final_status === 'PASS' ? '#4CAF50' : '#FF9800', 0.2)}`,
                                cursor: 'default',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: alpha(qc.final_status === 'PASS' ? '#4CAF50' : '#FF9800', 0.08),
                                  transform: 'translateY(-1px)'
                                }
                              }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    {qc.test_type}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {qc.product_name} • Tested by: {qc.tested_by_name}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Chip 
                                    label={qc.final_status}
                                    size="small"
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      borderRadius: 2,
                                      color: 'white',
                                      bgcolor: qc.final_status === 'PASS' ? '#4CAF50' : '#FF9800',
                                      mb: 0.5
                                    }}
                                  />
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    {formatDate(qc.test_datetime)}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </CardContent>
                        </Card>
                      </Grid>


                    </Grid>
                  </Box>
                )}

                {/* Inventory Analytics Section */}
                {activeSection === 'inventory' && (
                  <Box>
                    {/* Section Header */}
                    <Box sx={{ mb: 4, pb: 2, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <InventoryIcon sx={{ fontSize: 32, color: '#607D8B', mr: 2 }} />
                        <Typography variant="h4" sx={{ 
                          fontWeight: 500, 
                          color: '#607D8B', 
                          fontSize: '2rem',
                          letterSpacing: '-0.3px'
                        }}>
                          Inventory Analytics
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '1.1rem'
                      }}>
                        Stock levels, cost analysis, and inventory management insights
                      </Typography>
                    </Box>

                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                mr: 2
                              }}>
                                <AttachMoneyIcon sx={{ fontSize: 24, color: 'success.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Inventory Value
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'success.main',
                              mb: 1
                            }}>
                              {formatCurrency(dummyData.inventory.summary.totalValue)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Across all categories
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                mr: 2
                              }}>
                                <InventoryIcon sx={{ fontSize: 24, color: 'info.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Items
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'info.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.inventory.summary.totalItems)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Units in stock
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                mr: 2
                              }}>
                                <TrendingDownIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Low Stock Items
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'warning.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.inventory.summary.lowStockItems)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Need reordering
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                mr: 2
                              }}>
                                <AttachMoneyIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Average Cost
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'primary.main',
                              mb: 1
                            }}>
                              {formatCurrency(45.75)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Per unit
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Category Breakdown */}
                    <Grid container spacing={4} sx={{ mb: 4 }}>
                      <Grid item xs={12}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Inventory by Category
                            </Typography>
                            <Grid container spacing={3}>
                              {Object.entries(dummyData.inventory.summary.categories).map(([key, data]) => {
                                const categoryName = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                                const colors = {
                                  'bottles': '#2196F3',
                                  'caps': '#FF9800',
                                  'labels': '#4CAF50',
                                  'products': '#9C27B0',
                                  'rawMaterials': '#F44336',
                                  'supplies': '#607D8B'
                                };
                                const color = colors[key] || '#666';
                                
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={key}>
                                    <Box sx={{ 
                                      p: 3, 
                                      borderRadius: 2, 
                                      bgcolor: alpha(color, 0.05),
                                      border: `1px solid ${alpha(color, 0.2)}`,
                                      cursor: 'default',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        bgcolor: alpha(color, 0.08),
                                        border: `1px solid ${alpha(color, 0.3)}`,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(color, 0.15)}`
                                      }
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ 
                                          p: 1, 
                                          borderRadius: 1, 
                                          bgcolor: alpha(color, 0.1),
                                          mr: 2,
                                          color: color
                                        }}>
                                          {key === 'bottles' && <InventoryIcon sx={{ fontSize: 20 }} />}
                                          {key === 'caps' && <InventoryIcon sx={{ fontSize: 20 }} />}
                                          {key === 'labels' && <InventoryIcon sx={{ fontSize: 20 }} />}
                                          {key === 'products' && <ShoppingCartIcon sx={{ fontSize: 20 }} />}
                                          {key === 'rawMaterials' && <ScienceIcon sx={{ fontSize: 20 }} />}
                                          {key === 'supplies' && <InventoryIcon sx={{ fontSize: 20 }} />}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                          {categoryName}
                                        </Typography>
                                      </Box>
                                      <Typography variant="h5" sx={{ 
                                        fontWeight: 500, 
                                        color: color,
                                        mb: 1
                                      }}>
                                        {formatNumber(data.count)}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        {formatCurrency(data.value)} total value
                                      </Typography>
                                      <Box sx={{ 
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: alpha(color, 0.1),
                                        overflow: 'hidden'
                                      }}>
                                        <Box sx={{
                                          width: `${(data.value / dummyData.inventory.summary.totalValue) * 100}%`,
                                          height: '100%',
                                          bgcolor: color,
                                          borderRadius: 3
                                        }} />
                                      </Box>
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Data Tables */}
                    <Grid container spacing={4}>
                      {/* Most Expensive Items */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Most Expensive Items
                            </Typography>
                            <Stack spacing={2}>
                              {dummyData.inventory.mostExpensive.map((item, index) => (
                                <Box key={index} sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: alpha(theme.palette.grey[100], 0.5),
                                  border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                  }
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {item.name}
                                    </Typography>
                                    <Chip 
                                      label={item.type}
                                      size="small"
                                      sx={{ 
                                        bgcolor: alpha(theme.palette.info.main, 0.1),
                                        color: 'info.main',
                                        fontWeight: 500
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {item.supplier}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                                      {formatCurrency(item.unitCost)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      per unit
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Recent Transactions */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Recent Transactions
                            </Typography>
                            <Stack spacing={2}>
                              {dummyData.inventory.recentTransactions.map((transaction, index) => (
                                <Box key={transaction.id} sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: alpha(theme.palette.grey[100], 0.5),
                                  border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                  }
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {transaction.item}
                                    </Typography>
                                    <Chip 
                                      label={transaction.type}
                                      size="small"
                                      sx={{ 
                                        bgcolor: transaction.type === 'received' ? alpha('#4CAF50', 0.1) : alpha('#FF9800', 0.1),
                                        color: transaction.type === 'received' ? '#4CAF50' : '#FF9800',
                                        fontWeight: 500
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                    {transaction.category} • Qty: {formatNumber(transaction.quantity)}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                                      {formatCurrency(transaction.cost)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      {formatDate(transaction.date)}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Formulations Analytics Section */}
                {activeSection === 'formulations' && (
                  <Box>
                    {/* Section Header */}
                    <Box sx={{ mb: 4, pb: 2, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ScienceIcon sx={{ fontSize: 32, color: '#9C27B0', mr: 2 }} />
                        <Typography variant="h4" sx={{ 
                          fontWeight: 500, 
                          color: '#9C27B0', 
                          fontSize: '2rem',
                          letterSpacing: '-0.3px'
                        }}>
                          Formulation Analytics
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '1.1rem'
                      }}>
                        R&D progress, ingredient analysis, testing metrics, and formulation development insights
                      </Typography>
                    </Box>

                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                mr: 2
                              }}>
                                <ScienceIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Total Formulations
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'primary.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.formulations.totalFormulations)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Active formulations
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                mr: 2
                              }}>
                                <TrendingUpIcon sx={{ fontSize: 24, color: 'success.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Average Progress
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'success.main',
                              mb: 1
                            }}>
                              {dummyData.formulations.progressMetrics.averageProgress.toFixed(1)}%
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Completion rate
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                mr: 2
                              }}>
                                <AssessmentIcon sx={{ fontSize: 24, color: 'info.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Completed
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'info.main',
                              mb: 1
                            }}>
                              {formatNumber(dummyData.formulations.progressMetrics.completedFormulations)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Finished formulations
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} lg={3}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                mr: 2
                              }}>
                                <AssessmentIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                Test Pass Rate
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 500, 
                              color: 'warning.main',
                              mb: 1
                            }}>
                              {dummyData.formulations.testingMetrics.passRate.toFixed(1)}%
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Quality control
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Analytics Grid */}
                    <Grid container spacing={4}>
                      {/* Status Distribution */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Status Distribution
                            </Typography>
                            <Stack spacing={2}>
                              {dummyData.formulations.formulationsByStatus.map((item, index) => (
                                <Box key={item.status} sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: alpha(theme.palette.grey[100], 0.5),
                                  border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                  }
                                }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                      {item.status === 'approved' ? 'Approved' : 
                                       item.status === 'in_review' ? 'In Review' : 
                                       item.status === 'draft' ? 'Draft' : 
                                       item.status === 'rejected' ? 'Rejected' : item.status}
                                    </Typography>
                                    <Chip 
                                      label={item.count}
                                      size="small"
                                      sx={{ 
                                        bgcolor: item.status === 'approved' ? alpha('#4CAF50', 0.1) : 
                                                 item.status === 'in_review' ? alpha('#FF9800', 0.1) : 
                                                 item.status === 'draft' ? alpha('#2196F3', 0.1) : alpha('#F44336', 0.1),
                                        color: item.status === 'approved' ? '#4CAF50' : 
                                               item.status === 'in_review' ? '#FF9800' : 
                                               item.status === 'draft' ? '#2196F3' : '#F44336',
                                        fontWeight: 500
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ 
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: alpha(theme.palette.grey[300], 0.3),
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      width: `${(item.count / dummyData.formulations.totalFormulations) * 100}%`,
                                      height: '100%',
                                      bgcolor: item.status === 'approved' ? '#4CAF50' : 
                                               item.status === 'in_review' ? '#FF9800' : 
                                               item.status === 'draft' ? '#2196F3' : '#F44336',
                                      borderRadius: 4
                                    }} />
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Top Ingredients */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Most Used Ingredients
                            </Typography>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Ingredient</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Usage</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Avg %</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dummyData.formulations.topIngredients.map((ingredient, index) => (
                                    <TableRow key={ingredient.name} sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                                      }
                                    }}>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Chip 
                                            label={index + 1}
                                            size="small"
                                            sx={{ 
                                              mr: 1,
                                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                                              color: 'primary.main',
                                              fontWeight: 500,
                                              minWidth: 24
                                            }}
                                          />
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {ingredient.name}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                                        {ingredient.formulationCount} formulations
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                        {ingredient.averagePercentage.toFixed(2)}%
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Recent Activity */}
                      <Grid item xs={12}>
                        <Card sx={{ 
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: 'white',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
                          }
                        }}>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: 500, 
                              color: 'text.primary',
                              mb: 3
                            }}>
                              Recent Formulation Activity
                            </Typography>
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Formulation</TableCell>
                                    <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Status</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Progress</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Last Updated</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dummyData.formulations.recentActivity.map((activity) => (
                                    <TableRow key={activity.id} sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                                      }
                                    }}>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {activity.name}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={activity.status === 'approved' ? 'Approved' : 
                                                 activity.status === 'in_review' ? 'In Review' : 
                                                 activity.status === 'draft' ? 'Draft' : 
                                                 activity.status === 'rejected' ? 'Rejected' : activity.status}
                                          size="small"
                                          sx={{ 
                                            bgcolor: activity.status === 'approved' ? alpha('#4CAF50', 0.1) : 
                                                     activity.status === 'in_review' ? alpha('#FF9800', 0.1) : 
                                                     activity.status === 'draft' ? alpha('#2196F3', 0.1) : alpha('#F44336', 0.1),
                                            color: activity.status === 'approved' ? '#4CAF50' : 
                                                   activity.status === 'in_review' ? '#FF9800' : 
                                                   activity.status === 'draft' ? '#2196F3' : '#F44336',
                                            fontWeight: 500
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 40 }}>
                                            {activity.progress}%
                                          </Typography>
                                          <Box sx={{ 
                                            width: 80,
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                                            overflow: 'hidden'
                                          }}>
                                            <Box sx={{
                                              width: `${activity.progress}%`,
                                              height: '100%',
                                              bgcolor: theme.palette.primary.main,
                                              borderRadius: 3
                                            }} />
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                        {formatDate(activity.updated_at)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Placeholder sections for other analytics */}
                {!['sales', 'products', 'production', 'inventory', 'formulations'].includes(activeSection) && (
                  <Box sx={{ 
                    height: 400, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.grey[100], 0.3),
                    borderRadius: 3,
                    border: `2px dashed ${theme.palette.grey[300]}`
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      {(() => {
                        const currentItem = navigationItems.find(item => item.id === activeSection);
                        const IconComponent = currentItem?.icon || AssessmentIcon;
                        return (
                          <>
                            <IconComponent sx={{ fontSize: 64, color: currentItem?.color || 'text.secondary', mb: 2 }} />
                            <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                              {currentItem?.label} Preview
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                              Interactive analytics and insights coming soon
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Box>
      </Fade>
    </Box>
  );
};

export default DashboardShowCase;